import { useId, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import HtmlText from '../components/HtmlText';

/** Canonical recovery surface. Navigation and persistence remain owned by the session. */
export function FeedbackFlowSheet({
  open,
  title,
  body,
  children,
  actions,
  pending = false,
  onDismiss,
  container,
  contentWidth,
}: {
  open: boolean;
  title: string;
  body?: string;
  children: ReactNode;
  actions?: ReactNode;
  pending?: boolean;
  onDismiss?: () => void;
  container: () => HTMLElement | null;
  contentWidth: number;
}) {
  const titleId = useId();
  return (
    <Dialog
      open={open}
      container={container}
      fullWidth
      maxWidth="xs"
      aria-labelledby={titleId}
      slotProps={{ paper: { 'aria-busy': pending || undefined } }}
      onClose={() => {
        if (!pending) onDismiss?.();
      }}
      sx={{
        '& .MuiDialog-container': { alignItems: 'flex-end' },
        '& .MuiDialog-paper': {
          m: 0,
          width: '100%',
          maxWidth: contentWidth,
          maxHeight: 'calc(100dvh - 8px)',
          minHeight: 'min(440px, calc(100dvh - 8px))',
          borderRadius: '16px 16px 0 0',
          bgcolor: 'var(--cx-brand-surface, #fcfdfd)',
          color: 'var(--cx-brand-text, #1c252e)',
        },
      }}
    >
      <Box sx={{ pt: 0.5, px: 2, pb: 1.5, textAlign: 'center' }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 32,
            height: 4,
            borderRadius: 4,
            bgcolor: 'rgba(145,158,171,.32)',
            mx: 'auto',
            mb: 0.5,
          }}
        />
        <Box id={titleId}>
          <HtmlText
            html={title}
            variant="h6"
            sx={{ fontSize: 17, fontWeight: 600, lineHeight: '26px' }}
          />
        </Box>
        {body && (
          <HtmlText
            html={body}
            variant="body2"
            sx={{ fontSize: 14, lineHeight: '22px', '&&': { color: 'var(--cx-brand-text-muted)' } }}
          />
        )}
      </Box>
      <DialogContent sx={{ px: 2, pt: 0, pb: 1.5, minHeight: 0 }}>{children}</DialogContent>
      {actions && (
        <DialogActions
          sx={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: 'minmax(0, 1fr)',
            px: 1.25,
            pt: 1.25,
            pb: 'max(12px, env(safe-area-inset-bottom))',
            gap: 1.25,
            '& > :not(style)': { flex: 1, minWidth: 0 },
            '& > :not(style) ~ :not(style)': { ml: 0 },
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}
